import { defineStore } from 'pinia';
import type { HaltestelleWithQuelle } from '@/model/haltestelle';
import { getHaltestelleId } from '@/model/haltestelle';

const MRU_MAX_ENTRIES = 20;

export interface StationMRUEntry {
  station: HaltestelleWithQuelle;
  lastUsed: number;
  timesUsed: number;
}

export interface StationMRUEntries {
  [id: string]: StationMRUEntry;
}

export interface StationMRUState {
  stations: StationMRUEntries;
}

export const useStationMRUStore = defineStore('stationMRU', {
  state: (): StationMRUState => ({
    stations: {},
  }),
  persist: true,
  getters: {
    allEntrySorted: (state): StationMRUEntry[] => {
      const stations = Object.values(state.stations);
      stations.sort((a, b) => {
        if (b.timesUsed === a.timesUsed || b.timesUsed === 1) {
          return b.lastUsed - a.lastUsed;
        } else {
          return b.timesUsed - a.timesUsed;
        }
      });
      return stations;
    },
    allHaltestelleMRU(): HaltestelleWithQuelle[] {
      const stationMRUEntries: StationMRUEntry[] = this.allEntrySorted;
      return stationMRUEntries.map((entry) => entry.station);
    },
    allHaltestelleWithRl100(): HaltestelleWithQuelle[] {
      return this.allHaltestelleMRU.filter((stop) => stop.rl100 !== null);
    },
  },
  actions: {
    addStationToMRU(station: HaltestelleWithQuelle) {
      const stationId = getHaltestelleId(station);
      if (this.stations[stationId]) {
        this.stations[stationId].timesUsed += 1;
        this.stations[stationId].lastUsed = Date.now();
      } else {
        this.stations[stationId] = {
          station,
          lastUsed: Date.now(),
          timesUsed: 1,
        };
      }
    },
    trimMRU() {
      if (this.allEntrySorted.length > MRU_MAX_ENTRIES) {
        const newStations: StationMRUEntries = {};
        this.allEntrySorted
          .slice(0, MRU_MAX_ENTRIES)
          .forEach((entry) => (newStations[getHaltestelleId(entry.station)] = entry));
        this.$patch({
          stations: newStations,
        });
      }
    },
  },
});
